import { Pill, PillRadio, Tooltip, type PillVariant } from '@protoss/ui';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';
import * as React from 'react';
import type {
  BprGraphAreaPriority,
  BprGraphPriorityTier,
  PriorityLevel,
  SalesDealEstimateResult,
  SalesDealQuestionnaireSummary,
  ScopeMeta,
} from '@protoss/shared';
import { businessFunctionSequenceRank, parseScopeMeta } from '@protoss/shared';
import { salesApi } from '../services/salesApi';
import { buildHoursByField, computeHoursRange } from '../utils/estimateHours';
import { useScrollToFocusedAdhoc } from '../utils/useScrollToFocusedAdhoc';

// Ad-hoc catch-all section + the per-functional-area peer sections the server splits it into
// (splitAdhocSectionByGroup emits one `..__group__<slug>` section per scopeMeta.businessProcess).
// Mirrors ADHOC_SECTION_KEY / ADHOC_GROUP_SECTION_PREFIX / isAdhocSection in DealWorkspace.tsx (and
// the server twin in apps/api/src/services/sales/deal-adhoc-fields.ts). A scope item can live in ANY
// of these, so the table collects primaries from all of them - reading only the catch-all leaves the
// table empty once every item has a functional area.
const ADHOC_SECTION_KEY = 'ai_captured_questions';
const ADHOC_GROUP_SECTION_PREFIX = `${ADHOC_SECTION_KEY}__group__`;
function isAdhocSection(sectionKey: string | null | undefined): boolean {
  return (
    !!sectionKey &&
    (sectionKey === ADHOC_SECTION_KEY || sectionKey.startsWith(ADHOC_GROUP_SECTION_PREFIX))
  );
}

// Display label for the bucket of line-items with no functional area assigned.
const UNGROUPED_LABEL = 'Ungrouped';
// Sentinel React key for the ungrouped <tbody> (a real area name can never collide - it is user text).
const UNGROUPED_VALUE = '__ungrouped__';

// Compact, color-coded priority pills (abbreviated; `title` gives the accessible name + tooltip).
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'L', title: 'Low' },
  { value: 'medium', label: 'M', title: 'Medium' },
  { value: 'high', label: 'H', title: 'High' },
];

// Scope status as a compact 3-way toggle for the table column.
const SCOPE_OPTIONS = [
  { value: 'requested', label: 'Requested', title: 'Requested' },
  {
    value: 'ongoing',
    label: 'Ongoing',
    title: 'Ongoing discussion - deferred to a future session',
  },
  { value: 'mentioned', label: 'Not incl.', title: 'Not included in estimate' },
];

interface PrimaryField {
  fieldKey: string;
  label: string;
  displayOrder: number;
  scopeMeta: ScopeMeta;
}

/** One functional-area cluster of line-items (the Prioritization analogue of a capture group). */
interface AreaGroup {
  /** Trimmed functional-area name; '' for the Ungrouped bucket. */
  areaKey: string;
  /** Display label (area name, or "Ungrouped"). */
  label: string;
  fields: PrimaryField[];
  /** The matched knowledge-graph area rollup (BPR deals) - tier badge + finding counts. */
  graphPriority?: BprGraphAreaPriority;
  /**
   * Header emphasis: sections containing Critical / High findings are ALWAYS highlighted, even
   * though the section order is chronological. From the graph rollup when matched; without a
   * graph, a section whose items carry a 'high' customer/recommended priority gets 'high'.
   */
  highlight: 'critical' | 'high' | null;
}

function extractPrimaryFields(questionnaire: SalesDealQuestionnaireSummary | null): PrimaryField[] {
  if (!questionnaire) return [];
  const fields: PrimaryField[] = [];
  // Gather PRIMARY scope items from EVERY ad-hoc section (the catch-all + each functional-area peer).
  // `isAdhocPrimary` is a second guard so seeded template/metadata sections (Company, etc.) never leak.
  for (const section of questionnaire.sections) {
    if (!isAdhocSection(section.sectionKey)) continue;
    for (const group of section.groups) {
      for (const field of group.fields) {
        if (field.isAdhocPrimary) {
          fields.push({
            fieldKey: field.fieldKey,
            label: field.label,
            displayOrder: field.displayOrder,
            scopeMeta: parseScopeMeta(field.scopeMeta),
          });
        }
      }
    }
  }
  // Global displayOrder sort across sections keeps the reorder permutation contract whole and gives a
  // stable order that buildAreaGroups then re-clusters by functional area.
  fields.sort((a, b) => a.displayOrder - b.displayOrder);
  return fields;
}

/** The trimmed functional area of a line-item ('' when unassigned). */
function areaOf(field: PrimaryField): string {
  return field.scopeMeta.businessProcess?.trim() ?? '';
}

/** Loose match key joining free-text area names to graph area labels/slugs. */
function normalizeAreaName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Tier pill colour for the group-header knowledge-graph badge. */
function graphTierVariant(tier: BprGraphPriorityTier): PillVariant {
  switch (tier) {
    case 'Critical':
      return 'danger';
    case 'High':
      return 'warning';
    case 'Medium':
      return 'accent';
    default:
      return 'muted';
  }
}

/** Header emphasis for a group: graph finding counts first, item priorities as the fallback. */
function highlightOf(
  fields: PrimaryField[],
  graphPriority?: BprGraphAreaPriority
): 'critical' | 'high' | null {
  if (graphPriority) {
    if (graphPriority.findingCounts.Critical > 0) return 'critical';
    if (graphPriority.findingCounts.High > 0) return 'high';
    return null;
  }
  return fields.some(
    (f) => f.scopeMeta.customerPriority === 'high' || f.scopeMeta.recommendedPriority === 'high'
  )
    ? 'high'
    : null;
}

/**
 * Cluster the (already globally-ordered) line-items by functional area, mirroring the Questionnaire's
 * two-level grouping. Named areas follow the CHRONOLOGICAL business-function sequence (Lead to
 * Order -> Order to Cash -> Procure to Pay -> ... -> Record to Report -> System Integrations ->
 * Data Architecture; `businessFunctionSequenceRank`); areas outside the sequence trail in the
 * knowledge graph's decreasing priority order (BPR deals), then alphabetically; the Ungrouped
 * bucket is always last. Priority is still surfaced - each matched header carries its tier badge
 * and sections with Critical/High findings are highlighted via `highlight`. Within a group, items
 * keep their relative order in `orderedFields`, so a within-group reorder persists a sensible
 * global order via `reorderAdhocFields`.
 */
function buildAreaGroups(
  orderedFields: PrimaryField[],
  areaPriorities?: BprGraphAreaPriority[] | null
): AreaGroup[] {
  const byArea = new Map<string, PrimaryField[]>();
  for (const f of orderedFields) {
    const key = areaOf(f);
    const bucket = byArea.get(key);
    if (bucket) bucket.push(f);
    else byArea.set(key, [f]);
  }

  // Graph rank lookup: match a group's free-text area name onto the graph
  // area's label OR its functional-node slug ('functional.order_to_cash').
  const rankByName = new Map<string, { rank: number; area: BprGraphAreaPriority }>();
  (areaPriorities ?? []).forEach((area, rank) => {
    const entry = { rank, area };
    const byLabel = normalizeAreaName(area.label);
    if (byLabel && !rankByName.has(byLabel)) rankByName.set(byLabel, entry);
    const bySlug = normalizeAreaName(area.areaId.replace(/^functional\./, ''));
    if (bySlug && !rankByName.has(bySlug)) rankByName.set(bySlug, entry);
  });

  const named = [...byArea.keys()].filter((k) => k);
  named.sort((a, b) => {
    const seqA = businessFunctionSequenceRank(a);
    const seqB = businessFunctionSequenceRank(b);
    if (seqA !== seqB) return seqA - seqB;
    const rankA = rankByName.get(normalizeAreaName(a))?.rank ?? Number.MAX_SAFE_INTEGER;
    const rankB = rankByName.get(normalizeAreaName(b))?.rank ?? Number.MAX_SAFE_INTEGER;
    if (rankA !== rankB) return rankA - rankB;
    return a.localeCompare(b);
  });

  const groups: AreaGroup[] = named.map((key) => {
    const matched = rankByName.get(normalizeAreaName(key));
    const fields = byArea.get(key)!;
    return {
      areaKey: key,
      label: key,
      fields,
      highlight: highlightOf(fields, matched?.area),
      ...(matched ? { graphPriority: matched.area } : {}),
    };
  });
  const ungrouped = byArea.get('');
  if (ungrouped) {
    groups.push({
      areaKey: '',
      label: UNGROUPED_LABEL,
      fields: ungrouped,
      highlight: highlightOf(ungrouped),
    });
  }
  return groups;
}

export interface DealPrioritizationPaneProps {
  dealUuid: string;
  questionnaire: SalesDealQuestionnaireSummary | null;
  loading?: boolean;
  /** Passed down from DealWorkspace while the AI inference call is in flight. */
  inferring?: boolean;
  /** Saved estimate (effective data) so the Base hours column can show per-item hours; null = "-". */
  estimate?: SalesDealEstimateResult | null;
  /** fieldKey currently focused across the split view - this pane's matching row is highlighted. */
  focusedAdhocFieldKey?: string | null;
  /** Set the cross-pane focused fieldKey (clicking a row) so the Questionnaire scrolls to it. */
  onFocusAdhocField?: (fieldKey: string | null) => void;
  /** Reload the questionnaire after a reorder persists, so both panes re-sort to the new order. */
  onReorderComplete?: () => Promise<void> | void;
  /** Enable drag-to-reorder / move + the per-row area control (false for viewers, who cannot edit). */
  canReorder?: boolean;
  /** Immediately persist + live-sync the always-saved scope fields (Scope status, priorities, area). */
  onCommitScopeFields?: (
    fieldKey: string,
    patch: Partial<
      Pick<
        ScopeMeta,
        'scopeStatus' | 'customerPriority' | 'recommendedPriority' | 'businessProcess'
      >
    >
  ) => void;
  /**
   * BPR deals: the knowledge graph's per-area rollup (decreasing priority) - badges each
   * matched header with its tier, highlights sections holding Critical/High findings, and
   * breaks chronological-sequence ties for areas outside the canonical business-function
   * order. Null/absent (non-BPR, or no graph built) keeps chronological-then-alphabetical.
   */
  areaPriorities?: BprGraphAreaPriority[] | null;
}

export function DealPrioritizationPane({
  dealUuid,
  questionnaire,
  loading = false,
  inferring = false,
  estimate = null,
  focusedAdhocFieldKey = null,
  onFocusAdhocField,
  onReorderComplete,
  canReorder = false,
  onCommitScopeFields,
  areaPriorities = null,
}: DealPrioritizationPaneProps) {
  const primaryFields = React.useMemo(() => extractPrimaryFields(questionnaire), [questionnaire]);

  // Drag state. orderOverride holds the optimistic fieldKey order until the questionnaire reload
  // reflects it; dragId tracks the in-flight drag; dragOverKey / dragOverArea drive the drop
  // highlight for a row (reorder/move) vs a group header (reassign).
  const dragId = React.useRef<string | null>(null);
  const [dragOverKey, setDragOverKey] = React.useState<string | null>(null);
  const [dragOverArea, setDragOverArea] = React.useState<string | null>(null);
  const [orderOverride, setOrderOverride] = React.useState<string[] | null>(null);

  // Cross-pane focus: scroll + flash this pane's row when focusedAdhocFieldKey changes.
  const rootRef = React.useRef<HTMLDivElement>(null);
  useScrollToFocusedAdhoc(focusedAdhocFieldKey, rootRef);

  // A fresh questionnaire (e.g. after the reorder reload) carries the server order - drop the override.
  React.useEffect(() => {
    setOrderOverride(null);
  }, [questionnaire]);

  // Rows in effect order: the optimistic order while reordering, else the displayOrder-sorted fields.
  const orderedFields = React.useMemo(() => {
    if (!orderOverride) return primaryFields;
    const byKey = new Map(primaryFields.map((f) => [f.fieldKey, f]));
    const ordered: PrimaryField[] = [];
    for (const key of orderOverride) {
      const f = byKey.get(key);
      if (f) ordered.push(f);
    }
    // Defensive: append any primaries missing from the override (e.g. a concurrent add).
    for (const f of primaryFields) {
      if (!orderOverride.includes(f.fieldKey)) ordered.push(f);
    }
    return ordered;
  }, [primaryFields, orderOverride]);

  const areaGroups = React.useMemo(
    () => buildAreaGroups(orderedFields, areaPriorities),
    [orderedFields, areaPriorities]
  );
  const hoursByField = React.useMemo(() => buildHoursByField(estimate), [estimate]);
  const estimateRange = estimate?.estimateRange ?? null;

  // Persist a reorder, then reload the questionnaire so the Questionnaire pane re-sorts too.
  const persistReorder = async (orderedKeys: string[]) => {
    try {
      await salesApi.reorderAdhocFields(dealUuid, orderedKeys);
    } catch {
      // Leave the optimistic order visible; the reload (or the next one) corrects it.
    } finally {
      await onReorderComplete?.();
    }
  };

  // Reassign a line-item's functional area ('' = ungroup). Persists + live-syncs via the parent; the
  // pane re-clusters from the updated scopeMeta on the next render (no reload needed).
  const reassignArea = (fieldKey: string, areaKey: string) => {
    onCommitScopeFields?.(fieldKey, { businessProcess: areaKey });
  };

  const clearDragState = () => {
    dragId.current = null;
    setDragOverKey(null);
    setDragOverArea(null);
  };

  // Drop on a ROW: same area -> reorder; different area -> move into that row's area.
  const handleDropOnRow = (target: PrimaryField) => {
    const source = dragId.current;
    clearDragState();
    if (!source || source === target.fieldKey) return;
    const sourceField = orderedFields.find((f) => f.fieldKey === source);
    if (!sourceField) return;
    if (areaOf(sourceField) !== areaOf(target)) {
      reassignArea(source, areaOf(target));
      return;
    }
    const keys = orderedFields.map((f) => f.fieldKey);
    const from = keys.indexOf(source);
    const to = keys.indexOf(target.fieldKey);
    if (from < 0 || to < 0) return;
    const moved = keys.splice(from, 1)[0];
    if (moved === undefined) return;
    keys.splice(to, 0, moved);
    setOrderOverride(keys);
    void persistReorder(keys);
  };

  // Drop on a GROUP HEADER: reassign the dragged item to that header's area (no-op if already there).
  const handleDropOnHeader = (areaKey: string) => {
    const source = dragId.current;
    clearDragState();
    if (!source) return;
    const sourceField = orderedFields.find((f) => f.fieldKey === source);
    if (!sourceField || areaOf(sourceField) === areaKey) return;
    reassignArea(source, areaKey);
  };

  if (loading) {
    return <div className="deal-prioritization__empty-state">Loading...</div>;
  }

  const hasFields = primaryFields.length > 0;
  // Column count for the full-width group-header row's colspan.
  const colCount = (canReorder ? 1 : 0) + 8;

  if (!hasFields) {
    return (
      <div className="deal-prioritization" ref={rootRef}>
        <p className="deal-prioritization__empty-state">
          No scope items captured yet. Run a transcript analysis first to discover scope questions,
          then return here to analyze their priorities.
        </p>
      </div>
    );
  }

  // Running line-item number across all groups (matches the old flat numbering).
  let rowNumber = 0;

  return (
    <div className="deal-prioritization" ref={rootRef}>
      <div className="deal-prioritization__table-scroll">
        <table className="deal-prioritization__table">
          <thead>
            <tr>
              {canReorder && (
                <th
                  className="deal-prioritization__th deal-prioritization__th--drag"
                  aria-hidden="true"
                />
              )}
              <th className="deal-prioritization__th deal-prioritization__th--num">#</th>
              <th className="deal-prioritization__th">Summary</th>
              <th className="deal-prioritization__th deal-prioritization__th--problem">
                Problem / Gap
              </th>
              <th className="deal-prioritization__th deal-prioritization__th--solution">
                Proposed Solution
              </th>
              <th className="deal-prioritization__th deal-prioritization__th--priority">
                Customer
              </th>
              <th className="deal-prioritization__th deal-prioritization__th--priority">
                Recommended
              </th>
              <th className="deal-prioritization__th deal-prioritization__th--scope">Scope</th>
              <th className="deal-prioritization__th deal-prioritization__th--hours">Base hours</th>
            </tr>
          </thead>
          {areaGroups.map((group) => (
            <tbody key={group.areaKey || UNGROUPED_VALUE}>
              <tr
                className={[
                  'deal-prioritization__group-header',
                  group.highlight ? `deal-prioritization__group-header--${group.highlight}` : '',
                  dragOverArea === group.areaKey
                    ? 'deal-prioritization__group-header--drag-over'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onDragOver={
                  canReorder
                    ? (e) => {
                        e.preventDefault();
                        if (dragOverArea !== group.areaKey) setDragOverArea(group.areaKey);
                      }
                    : undefined
                }
                onDrop={canReorder ? () => handleDropOnHeader(group.areaKey) : undefined}
              >
                <td className="deal-prioritization__group-cell" colSpan={colCount}>
                  <span className="deal-prioritization__group-name">{group.label}</span>
                  {group.graphPriority ? (
                    <Tooltip.Simple
                      label={`Knowledge-graph priority: ${group.graphPriority.findingCounts.total} finding${group.graphPriority.findingCounts.total === 1 ? '' : 's'}, score ${group.graphPriority.score}`}
                    >
                      <span className="deal-prioritization__group-tier">
                        <Pill variant={graphTierVariant(group.graphPriority.topTier)}>
                          {group.graphPriority.topTier}
                        </Pill>
                      </span>
                    </Tooltip.Simple>
                  ) : null}
                  {group.graphPriority &&
                  (group.graphPriority.findingCounts.Critical > 0 ||
                    group.graphPriority.findingCounts.High > 0) ? (
                    <span className="deal-prioritization__group-alert">
                      {[
                        group.graphPriority.findingCounts.Critical > 0
                          ? `${group.graphPriority.findingCounts.Critical} Critical`
                          : '',
                        group.graphPriority.findingCounts.High > 0
                          ? `${group.graphPriority.findingCounts.High} High`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  ) : null}
                  <span className="deal-prioritization__group-count">
                    {group.fields.length} {group.fields.length === 1 ? 'item' : 'items'}
                  </span>
                </td>
              </tr>
              {group.fields.map((field) => {
                rowNumber += 1;
                // Read effective values straight from the (live) in-memory scopeMeta - commits land
                // there optimistically, so no separate local-override state is needed.
                const meta: ScopeMeta = field.scopeMeta ?? {};
                const customerPriority = meta.customerPriority ?? null;
                const recommendedPriority = meta.recommendedPriority ?? null;
                const scopeStatus = meta.scopeStatus ?? 'requested';
                // "Not included in estimate" (mentioned) is EXCLUDED - strike its label through.
                // "Ongoing discussion" (ongoing) is deferred but still live, so it is NOT struck.
                const excluded = scopeStatus === 'mentioned';
                // Both are non-costed (not in the estimate) - that drives the inconsistency flag below.
                const outOfScope = scopeStatus === 'mentioned' || scopeStatus === 'ongoing';

                // Inconsistency: customer rates important but item not in estimate
                const isInconsistent =
                  (customerPriority === 'high' || customerPriority === 'medium') && outOfScope;

                const hours = hoursByField.get(field.fieldKey);
                const range = hours != null ? computeHoursRange(hours, estimateRange) : null;

                const rowClass = [
                  'deal-prioritization__row',
                  focusedAdhocFieldKey === field.fieldKey
                    ? 'deal-prioritization__row--selected'
                    : '',
                  dragOverKey === field.fieldKey ? 'deal-prioritization__row--drag-over' : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <tr
                    key={field.fieldKey}
                    data-adhoc-fieldkey={field.fieldKey}
                    className={rowClass}
                    onClick={() => onFocusAdhocField?.(field.fieldKey)}
                    onDragOver={
                      canReorder
                        ? (e) => {
                            e.preventDefault();
                            if (dragOverKey !== field.fieldKey) setDragOverKey(field.fieldKey);
                            if (dragOverArea !== null) setDragOverArea(null);
                          }
                        : undefined
                    }
                    onDrop={canReorder ? () => handleDropOnRow(field) : undefined}
                  >
                    {canReorder && (
                      <td className="deal-prioritization__td deal-prioritization__td--drag">
                        <span
                          className="deal-prioritization__drag"
                          aria-label="Drag to reorder or move to another functional area"
                          draggable
                          onDragStart={(e) => {
                            dragId.current = field.fieldKey;
                            e.dataTransfer.effectAllowed = 'move';
                            // Drag the whole row as the ghost image, not just the handle.
                            const row = e.currentTarget.closest('tr');
                            if (row) e.dataTransfer.setDragImage(row, 0, 0);
                          }}
                          onDragEnd={clearDragState}
                        >
                          <DragHandleDots2Icon />
                        </span>
                      </td>
                    )}
                    <td className="deal-prioritization__td deal-prioritization__td--num">
                      {rowNumber}
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--label">
                      <span
                        className={
                          excluded
                            ? 'deal-prioritization__label deal-prioritization__label--out-of-scope'
                            : 'deal-prioritization__label'
                        }
                      >
                        {field.label}
                      </span>
                      {isInconsistent && (
                        <Tooltip.Simple label="Customer rates this as important but it is not included in the estimate. Consider changing scope status to Requested.">
                          <span
                            className="deal-prioritization__inconsistency-badge"
                            aria-label="Priority inconsistency: not in estimate"
                          >
                            !
                          </span>
                        </Tooltip.Simple>
                      )}
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--problem">
                      {meta.problem ? (
                        <span>{meta.problem}</span>
                      ) : (
                        <span className="deal-prioritization__empty">-</span>
                      )}
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--solution">
                      {meta.proposedSolution ? (
                        <span>{meta.proposedSolution}</span>
                      ) : (
                        <span className="deal-prioritization__empty">-</span>
                      )}
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--priority">
                      <PillRadio
                        options={PRIORITY_OPTIONS}
                        selectedValue={customerPriority}
                        onSelectionChange={(v) => {
                          if (v)
                            onCommitScopeFields?.(field.fieldKey, {
                              customerPriority: v as PriorityLevel,
                            });
                        }}
                        disabled={inferring}
                        className="deal-prioritization__priority-pill priority-pills"
                      />
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--priority">
                      <PillRadio
                        options={PRIORITY_OPTIONS}
                        selectedValue={recommendedPriority}
                        onSelectionChange={(v) => {
                          if (v)
                            onCommitScopeFields?.(field.fieldKey, {
                              recommendedPriority: v as PriorityLevel,
                            });
                        }}
                        disabled={inferring}
                        className="deal-prioritization__priority-pill priority-pills"
                      />
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--scope">
                      <PillRadio
                        options={SCOPE_OPTIONS}
                        selectedValue={scopeStatus}
                        onSelectionChange={(v) =>
                          onCommitScopeFields?.(field.fieldKey, {
                            scopeStatus:
                              v === 'mentioned'
                                ? 'mentioned'
                                : v === 'ongoing'
                                  ? 'ongoing'
                                  : 'requested',
                          })
                        }
                        disabled={inferring}
                        className="deal-prioritization__scope-pill"
                      />
                    </td>
                    <td className="deal-prioritization__td deal-prioritization__td--hours">
                      {hours == null ? (
                        <span className="deal-prioritization__empty">-</span>
                      ) : range ? (
                        <span className="deal-prioritization__hours-stack">
                          <span>{hours.toLocaleString()}h</span>
                          <span className="deal-prioritization__hours-range">
                            {range.low.toLocaleString()}h{' '}|{' '}
                            {range.high.toLocaleString()}h
                          </span>
                        </span>
                      ) : (
                        <span>{hours.toLocaleString()}h</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
}
